import { useFormik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Alert,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import * as Yup from "yup";
import { toast } from "react-toastify";
import Select from "react-select";

import { ActivityButton } from "../../../../components/Common";
import {
  ChangeSubscriptionRequest,
  ChangeSubscriptionResponse,
  LocalizedProperty,
  PaginateQuery,
  PhoneDto,
  SubscriptionBasicInformationDto,
} from "../../../../interfaces";
import { EducationalStageDto } from "../../../../interfaces/curriculum";
import { useGetCourseSelectListQuery } from "../../../../store/api/courseApi";
import { useGetCurriculumSelectListQuery } from "../../../../store/api/curriculumApi";
import { useGetEducationSystemSelectListQuery } from "../../../../store/api/educationSystemApi";
import { useGetEducationalStagesQuery } from "../../../../store/api/educationalStageApi";
import { useChangeSubscriptionMutation } from "../../../../store/api/subscriptionApi";
import { SelectListDto } from "../../../../models";

type TProps = {
  userId: string;
  show: boolean;
  onCloseClick: (event: any) => void;
  name: LocalizedProperty | null;
  phoneNumber: PhoneDto;
  currentSubscription: SubscriptionBasicInformationDto;
  onBack: () => void;
};

const labelOf = (
  item:
    | {
        value?: { en?: string; ar?: string };
        name?: { en?: string; ar?: string };
      }
    | null
    | undefined,
  langKey: "en" | "ar",
) =>
  item?.value?.[langKey] ||
  item?.value?.en ||
  item?.name?.[langKey] ||
  item?.name?.en ||
  "";

export const ChangeSubscriptionModal: React.FC<TProps> = ({
  userId,
  show,
  onCloseClick,
  name,
  phoneNumber,
  currentSubscription,
  onBack,
}) => {
  const { t, i18n } = useTranslation();
  const langKey = i18n.language === "ar" ? "ar" : "en";

  const [
    changeSubscription,
    { isLoading: isLoadingChangeSubscription, error: errorChangeSubscription },
  ] = useChangeSubscriptionMutation();

  const [educationSystemId, setEducationSystemId] = useState("");
  const [curriculumId, setCurriculumId] = useState("");
  const [courseId, setCourseId] = useState("");
  const [stageId, setStageId] = useState("");
  const [gradeId, setGradeId] = useState("");
  const [selectedPlan, setSelectedPlan] = useState<any>();
  const [options, setOptions] = useState<any>([]);
  const [warnings, setWarning] = useState<string>("");
  const [hierarchySeeded, setHierarchySeeded] = useState(false);

  const { data: educationSystems } = useGetEducationSystemSelectListQuery(
    undefined,
    { skip: !show },
  );
  const { data: curricula } = useGetCurriculumSelectListQuery(
    educationSystemId || undefined,
    { skip: !show || !educationSystemId },
  );
  const { data: courses } = useGetCourseSelectListQuery(undefined, {
    skip: !show,
  });

  const stagesQuery = useMemo(() => {
    const q = new PaginateQuery();
    q.limit = 1000;
    q.page = 1;
    q.sortBy = "sequence_number:ASC";
    q.filter = {
      curriculumId,
      courseId,
    };
    return q;
  }, [curriculumId, courseId]);

  const { data: stagesResponse } = useGetEducationalStagesQuery(stagesQuery, {
    skip: !show || !curriculumId || !courseId,
  });
  const stages: EducationalStageDto[] = stagesResponse?.data || [];

  const selectedStage = useMemo(
    () => stages.find((s) => s.id === stageId),
    [stages, stageId],
  );
  const grades = selectedStage?.grades || [];

  const form = useFormik({
    enableReinitialize: true,
    initialValues: {
      gradeId: "",
      planId: "",
    },
    validationSchema: Yup.object({
      gradeId: Yup.string().required(
        t("isNotEmpty", {
          displayName: "grade",
        }),
      ),
      planId: Yup.string().required(
        t("isNotEmpty", {
          displayName: "plan",
        }),
      ),
    }),
    onSubmit: async (values) => {
      const request: ChangeSubscriptionRequest = {
        studentId: userId,
        destinationGradeId: values.gradeId,
        selectedPlanId: values.planId,
        currentSubscriptionId: currentSubscription.subscriptionId,
      };
      try {
        const result = await changeSubscription(request).unwrap();
        const {
          concentMessage,
          purchaseablePlans,
          changeDone,
        }: ChangeSubscriptionResponse = result;
        if (changeDone) {
          onClose();
          toast.success("subscriptionChangedSuccess", {
            autoClose: 3000,
          });
        } else {
          if (concentMessage) setWarning(concentMessage);
          setOptions(
            purchaseablePlans?.map((item: SelectListDto) => {
              return {
                label: i18n.language === "ar" ? item.value.ar : item.value.en,
                value: item.id,
              };
            }),
          );
        }
      } catch (err: any) {
        toast.error(err?.data?.message || "An error occurred", {
          autoClose: 3000,
        });
      }
    },
  });

  const clearPlanState = () => {
    setOptions([]);
    setSelectedPlan(null);
    setWarning("");
    form.setFieldValue("planId", "");
  };

  const loadPlansForGrade = async (destinationGradeId: string) => {
    if (!destinationGradeId) return;
    clearPlanState();
    const request: ChangeSubscriptionRequest = {
      studentId: userId,
      destinationGradeId,
      currentSubscriptionId: currentSubscription.subscriptionId,
    };
    try {
      const result = await changeSubscription(request).unwrap();
      const {
        concentMessage,
        purchaseablePlans,
        changeDone,
      }: ChangeSubscriptionResponse = result;
      if (!changeDone) {
        if (concentMessage) setWarning(concentMessage);
        setOptions(
          purchaseablePlans?.map((item: SelectListDto) => {
            return {
              label: i18n.language === "ar" ? item.value.ar : item.value.en,
              value: item.id,
            };
          }),
        );
      }
    } catch (err: any) {
      toast.error(err?.data?.message || "An error occurred", {
        autoClose: 3000,
      });
    }
  };

  const resetHierarchy = () => {
    setEducationSystemId("");
    setCurriculumId("");
    setCourseId("");
    setStageId("");
    setGradeId("");
    setHierarchySeeded(false);
  };

  const onClose = () => {
    form.resetForm();
    resetHierarchy();
    clearPlanState();
    onCloseClick(!show);
  };

  const onChangePlan = (changedValue: any) => {
    setSelectedPlan(changedValue);
    form.setFieldValue("planId", changedValue?.value || "");
  };

  // Seed hierarchy from current subscription when opening
  useEffect(() => {
    if (!show || hierarchySeeded) return;
    setEducationSystemId(currentSubscription.educationSystem?.id || "");
    setCurriculumId(currentSubscription.curriculum?.id || "");
    setCourseId(currentSubscription.course?.id || "");
    setStageId(currentSubscription.educationalStage?.id || "");
    setGradeId(currentSubscription.grade?.id || "");
    form.setFieldValue("gradeId", currentSubscription.grade?.id || "");
    setHierarchySeeded(true);
  }, [show, currentSubscription, hierarchySeeded]);

  useEffect(() => {
    if (!show) {
      resetHierarchy();
      clearPlanState();
      form.resetForm();
      return;
    }
  }, [show]);

  useEffect(() => {
    if (!show || !hierarchySeeded) return;
    if (!educationSystems?.length) return;
    if (
      educationSystemId &&
      educationSystems.some((s: any) => s.id === educationSystemId)
    ) {
      return;
    }
    setEducationSystemId(educationSystems[0].id);
  }, [show, hierarchySeeded, educationSystems, educationSystemId]);

  useEffect(() => {
    if (!show || !hierarchySeeded) return;
    if (!curricula?.length) {
      if (curriculumId) setCurriculumId("");
      return;
    }
    if (curriculumId && curricula.some((c: any) => c.id === curriculumId)) {
      return;
    }
    setCurriculumId(curricula[0].id);
  }, [show, hierarchySeeded, curricula, curriculumId]);

  useEffect(() => {
    if (!show || !hierarchySeeded) return;
    if (!courses?.length) return;
    if (courseId && courses.some((c: any) => c.id === courseId)) return;
    const preferred =
      courses.find((c: any) => c.id === currentSubscription.course?.id) ||
      courses[0];
    setCourseId(preferred.id);
  }, [
    show,
    hierarchySeeded,
    courses,
    courseId,
    currentSubscription.course?.id,
  ]);

  useEffect(() => {
    if (!show || !hierarchySeeded) return;
    if (!stages.length) {
      if (stageId) setStageId("");
      return;
    }
    if (stageId && stages.some((s) => s.id === stageId)) return;
    const preferred =
      stages.find((s) => s.id === currentSubscription.educationalStage?.id) ||
      stages.find((s) =>
        s.grades?.some((g) => g.id === currentSubscription.grade?.id),
      ) ||
      stages.find((s) => s.grades?.length > 0) ||
      stages[0];
    setStageId(preferred.id);
  }, [
    show,
    hierarchySeeded,
    stages,
    stageId,
    currentSubscription.educationalStage?.id,
    currentSubscription.grade?.id,
  ]);

  useEffect(() => {
    if (!show || !hierarchySeeded) return;
    if (!grades.length) {
      if (gradeId) {
        setGradeId("");
        form.setFieldValue("gradeId", "");
      }
      return;
    }
    if (gradeId && grades.some((g) => g.id === gradeId)) return;
    const preferred =
      grades.find((g) => g.id === currentSubscription.grade?.id) || grades[0];
    setGradeId(preferred.id);
    form.setFieldValue("gradeId", preferred.id);
  }, [show, hierarchySeeded, grades, gradeId, currentSubscription.grade?.id]);

  // Load plans when destination grade changes (after seed)
  useEffect(() => {
    if (!show || !hierarchySeeded || !gradeId) return;
    form.setFieldValue("gradeId", gradeId);
    loadPlansForGrade(gradeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gradeId]);

  const pathLabel = [
    labelOf(
      educationSystems?.find((s: any) => s.id === educationSystemId),
      langKey,
    ),
    labelOf(
      curricula?.find((c: any) => c.id === curriculumId),
      langKey,
    ),
    labelOf(
      courses?.find((c: any) => c.id === courseId),
      langKey,
    ),
    selectedStage?.name?.[langKey] || selectedStage?.name?.en,
    grades.find((g) => g.id === gradeId)?.name?.[langKey] ||
      grades.find((g) => g.id === gradeId)?.name?.en,
  ]
    .filter(Boolean)
    .join(" / ");

  const currentPath = [
    labelOf(currentSubscription.educationSystem, langKey),
    labelOf(currentSubscription.curriculum, langKey),
    labelOf(currentSubscription.course, langKey),
    labelOf(currentSubscription.educationalStage, langKey),
    labelOf(currentSubscription.grade, langKey),
  ]
    .filter(Boolean)
    .join(" / ");

  return (
    <Modal
      id="updateSubscription"
      isOpen={show}
      toggle={() => {
        onClose();
      }}
      centered
      size="lg"
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
          return false;
        }}
        action="#"
      >
        <ModalHeader
          toggle={() => {
            onClose();
          }}
        >
          {t("updateSubscription")}
        </ModalHeader>
        {errorChangeSubscription && (
          <Alert color="danger" className="rounded-0 mb-0">
            <p className="mb-0">
              {(errorChangeSubscription as any)?.data?.message ||
                "An error occurred"}
            </p>
          </Alert>
        )}
        <ModalBody>
          <Row>
            <Col className="border border-2p p-3">
              <h5>{t("student")}</h5>
              <p>
                <strong>{t("name")}</strong>
                {": "}
                {name && name.en && name.ar
                  ? i18n.language === "ar"
                    ? name.ar
                    : name.en
                  : "n/a"}
              </p>
              <p>
                <strong>
                  {t("phoneNumber")} {": "}
                </strong>
                <a
                  style={{ cursor: "pointer" }}
                  href={`tel:+${phoneNumber?.countryCode}${phoneNumber?.phoneNumber}`}
                >
                  <u dir="ltr">{`+${phoneNumber?.countryCode}${phoneNumber?.phoneNumber}`}</u>
                </a>
              </p>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col className="border border-2p p-3">
              <h5 className="text-center">{t("currentSubscription")}</h5>
              <p>
                <strong>{t("plan")}: </strong>
                {i18n.language === "ar"
                  ? currentSubscription.name?.ar
                  : currentSubscription.name?.en}
              </p>
              {currentPath ? (
                <p className="text-muted mb-2">{currentPath}</p>
              ) : (
                <>
                  <p>
                    <strong>{t("course")}: </strong>
                    {i18n.language === "ar"
                      ? currentSubscription.course?.name?.ar
                      : currentSubscription.course?.name?.en}
                  </p>
                  <p>
                    <strong>{t("grade")}: </strong>
                    {i18n.language === "ar"
                      ? currentSubscription.grade?.name?.ar
                      : currentSubscription.grade?.name?.en}
                  </p>
                </>
              )}
              <p>
                {new Date(currentSubscription.startAt).toLocaleDateString()} -{" "}
                {new Date(currentSubscription.endAt).toLocaleDateString()}
              </p>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col className="border border-2p p-3">
              <h5 className="text-center">{t("changeSubscriptionTo")}</h5>

              <Row className="g-2 mb-2">
                <Col md={4}>
                  <Label className="form-label">
                    {t("educationSystem")}
                    <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="select"
                    value={educationSystemId}
                    onChange={(e) => {
                      setEducationSystemId(e.target.value);
                      setCurriculumId("");
                      setStageId("");
                      setGradeId("");
                      form.setFieldValue("gradeId", "");
                      clearPlanState();
                    }}
                  >
                    {!educationSystems?.length ? (
                      <option value="">{t("selectEducationSystem")}</option>
                    ) : null}
                    {(educationSystems || []).map((s: any) => (
                      <option key={s.id} value={s.id}>
                        {labelOf(s, langKey)}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={4}>
                  <Label className="form-label">
                    {t("curriculumItem")}
                    <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="select"
                    value={curriculumId}
                    disabled={!educationSystemId}
                    onChange={(e) => {
                      setCurriculumId(e.target.value);
                      setStageId("");
                      setGradeId("");
                      form.setFieldValue("gradeId", "");
                      clearPlanState();
                    }}
                  >
                    {!curricula?.length ? (
                      <option value="">{t("selectCurriculum")}</option>
                    ) : null}
                    {(curricula || []).map((c: any) => (
                      <option key={c.id} value={c.id}>
                        {labelOf(c, langKey)}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={4}>
                  <Label className="form-label">
                    {t("course")}
                    <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="select"
                    value={courseId}
                    onChange={(e) => {
                      setCourseId(e.target.value);
                      setStageId("");
                      setGradeId("");
                      form.setFieldValue("gradeId", "");
                      clearPlanState();
                    }}
                  >
                    {!courses?.length ? (
                      <option value="">{t("selectCourse")}</option>
                    ) : null}
                    {(courses || []).map((c: any) => (
                      <option key={c.id} value={c.id}>
                        {labelOf(c, langKey)}
                      </option>
                    ))}
                  </Input>
                </Col>
              </Row>

              <Row className="g-2 mb-2">
                <Col md={6}>
                  <Label className="form-label">
                    {t("educationalStages")}
                    <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="select"
                    value={stageId}
                    disabled={!curriculumId || !courseId}
                    onChange={(e) => {
                      setStageId(e.target.value);
                      setGradeId("");
                      form.setFieldValue("gradeId", "");
                      clearPlanState();
                    }}
                  >
                    {!stages.length ? (
                      <option value="">{t("selectEducationalStage")}</option>
                    ) : null}
                    {stages.map((stage) => (
                      <option key={stage.id} value={stage.id}>
                        {stage.name[langKey] || stage.name.en}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={6}>
                  <Label className="form-label">
                    {t("grade")}
                    <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="select"
                    id="gradeId"
                    name="gradeId"
                    value={gradeId}
                    disabled={!stageId}
                    onChange={(e) => {
                      const nextGradeId = e.target.value;
                      setGradeId(nextGradeId);
                      form.setFieldValue("gradeId", nextGradeId);
                      form.setFieldTouched("gradeId", true);
                    }}
                    onBlur={form.handleBlur}
                    invalid={!!(form.touched.gradeId && form.errors.gradeId)}
                  >
                    {!grades.length ? (
                      <option value="">{t("selectGrade")}</option>
                    ) : null}
                    {grades.map((grade) => (
                      <option key={grade.id} value={grade.id}>
                        {grade.name[langKey] || grade.name.en}
                      </option>
                    ))}
                  </Input>
                  {form.touched.gradeId && form.errors.gradeId ? (
                    <FormFeedback type="invalid">
                      {form.errors.gradeId}
                    </FormFeedback>
                  ) : null}
                </Col>
              </Row>

              {pathLabel ? (
                <small className="text-muted d-block mb-3">{pathLabel}</small>
              ) : null}

              <div className="mb-3">
                <Label htmlFor="planId" className="form-label">
                  {t("plans")}
                  <span className="text-danger">*</span>
                </Label>
                <Select
                  placeholder={t("filterByPlan")}
                  closeMenuOnSelect={true}
                  value={selectedPlan}
                  onChange={onChangePlan}
                  isMulti={false}
                  options={options}
                  isDisabled={!gradeId}
                />
                {form.touched.planId && form.errors.planId ? (
                  <FormFeedback type="invalid" className="d-block">
                    {form.errors.planId}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
          </Row>
          {warnings && (
            <Row className="mt-2">
              <Col className="border border-2p p-3">
                <h5 className="text-center">{t("warnings")}</h5>
                <p>{warnings}</p>
              </Col>
            </Row>
          )}
        </ModalBody>
        <ModalFooter>
          <Link
            to={"#"}
            onClick={onBack}
            className="btn btn-link link-success fw-medium"
          >
            <i
              className={`${
                i18n.language === "ar"
                  ? "ri-arrow-right-line"
                  : "ri-arrow-left-line"
              } me-1 align-middle`}
            ></i>{" "}
            {t("backToSubscriptions")}
          </Link>
          <Link
            to={"#"}
            onClick={onClose}
            className="btn btn-link link-success fw-medium"
          >
            <i className="ri-close-line me-1 align-middle"></i> {t("close")}
          </Link>
          <ActivityButton
            title={t("save")}
            inProgressTitle="saving"
            color="success"
            className="btn btn-success"
            type="submit"
            inProgress={isLoadingChangeSubscription}
            disabled={isLoadingChangeSubscription || !gradeId || !selectedPlan}
          />
        </ModalFooter>
      </Form>
    </Modal>
  );
};
